import { useState } from "react";
import { format } from "date-fns";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { formatDateBR } from "@/lib/date-utils";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import type { ReceivableRow } from "@/hooks/useRevenueReport";
import { exportToExcel } from "@/lib/utils/export";

interface RevenueReceivablesProps {
  data: ReceivableRow[];
  total: number;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

type StatusFilter = "all" | "ABERTA" | "upcoming" | "overdue";

function OriginCell({ row }: { row: ReceivableRow }) {
  const { t } = useTranslation("reports");
  if (!row.origin) return <span className="text-muted-foreground">{t("receivableOriginNone")}</span>;
  const label = t("receivableOriginStayPlan", {
    module: t(row.origin.module === "hotel" ? "receivableModuleHotel" : "receivableModuleDaycare"),
    pet: row.petName,
  });
  if (!row.origin.petId) return <span>{label}</span>;
  return <Link to={`/patients/pets/${row.origin.petId}`} className="hover:underline">{label}</Link>;
}

export function RevenueReceivables({ data, total: _total }: RevenueReceivablesProps) {
  const { t } = useTranslation("reports");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const filtered =
    statusFilter === "all" ? data
    : statusFilter === "overdue" ? data.filter((r) => r.isOverdue)
    : statusFilter === "upcoming" ? data.filter((r) => r.dueOn !== null && !r.isOverdue)
    : data.filter((r) => r.status === statusFilter);
  const filteredTotal = filtered.reduce((s, r) => s + r.total, 0);

  function handleExport() {
    exportToExcel({
      rows: filtered.map((r) => ({
        [t("receivableOwner")]: r.ownerName,
        [t("receivablePet")]: r.petName,
        [t("receivableSaleNumber")]: r.saleNumber,
        [t("receivableDate")]: r.createdAt ? format(new Date(r.createdAt), "dd/MM/yyyy") : "—",
        [t("receivableDueDate")]: r.dueOn ? formatDateBR(r.dueOn) : "—",
        [t("receivableOrigin")]: r.origin
          ? t("receivableOriginStayPlan", {
              module: t(r.origin.module === "hotel" ? "receivableModuleHotel" : "receivableModuleDaycare"),
              pet: r.petName,
            })
          : t("receivableOriginNone"),
        [t("receivableAmount")]: r.total,
        [t("receivableStatus")]: t(`receivableStatusLabel.${r.status}`),
      })),
      sheetName: t("receivablesTitle"),
      fileName: "contas-a-receber.xlsx",
    });
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{t("receivablesTitle")}</CardTitle>
            <CardDescription>{t("receivablesDesc")}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as StatusFilter)}
            >
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("filterAll")}</SelectItem>
                <SelectItem value="ABERTA">{t("receivableStatusLabel.ABERTA")}</SelectItem>
                <SelectItem value="upcoming">{t("receivableFilterUpcoming")}</SelectItem>
                <SelectItem value="overdue">{t("receivableFilterOverdue")}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={filtered.length === 0}
            >
              <Download className="h-4 w-4 mr-1" />
              {t("export")}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">
            {t("receivablesEmpty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("receivableOwner")}</TableHead>
                  <TableHead>{t("receivablePet")}</TableHead>
                  <TableHead>{t("receivableSaleNumber")}</TableHead>
                  <TableHead>{t("receivableDate")}</TableHead>
                  <TableHead>{t("receivableDueDate")}</TableHead>
                  <TableHead>{t("receivableOrigin")}</TableHead>
                  <TableHead className="text-right">{t("receivableAmount")}</TableHead>
                  <TableHead>{t("receivableStatus")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow key={row.saleId}>
                    <TableCell className="font-medium">{row.ownerName}</TableCell>
                    <TableCell>{row.petName}</TableCell>
                    <TableCell className="font-mono text-xs">{row.saleNumber}</TableCell>
                    <TableCell className="text-sm">
                      {row.createdAt ? format(new Date(row.createdAt), "dd/MM/yyyy") : "—"}
                    </TableCell>
                    <TableCell className={`text-sm ${row.isOverdue ? "text-destructive font-medium" : ""}`}>
                      {row.dueOn ? formatDateBR(row.dueOn) : "—"}
                    </TableCell>
                    <TableCell className="text-sm"><OriginCell row={row} /></TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(row.total)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={row.status === "ABERTA" ? "outline" : "secondary"}
                        className="text-xs"
                      >
                        {t(`receivableStatusLabel.${row.status}`)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="px-4 py-3 border-t flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {t("receivablesCount", { count: filtered.length })}
          </span>
          <span className="font-bold">{formatCurrency(filteredTotal)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
